import { Component, inject, input, OnInit, output } from '@angular/core';
import { MatCard, MatCardContent, MatCardHeader, MatCardTitle } from '@angular/material/card';
import { AsyncPipe, DatePipe } from '@angular/common';
import { UserIconComponent } from '../../../components/sb-client/user-icon/user-icon.component';
import { LastMessage, LastMessageService } from '../../../services/lastMessage/last-message.service';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { MediaService } from '../../../services/media/media.service';
import { UserService } from '../../../services/user/user.service';
import { RoomMembershipState } from '../../../../shared-api/roomMembershipState';
import { DateUtils } from '../../../services/util';
import { SpoilerChunksPipe } from '../../pipes/spoilerChuncks.pipe';
import { SpoilerComponent } from '../../../components/sb-client/sb-spoiler/sb-spoiler.component';
import { Room as Modelroom } from '../../../model/room';
import { RoomMember } from '../../../../shared-api/roomMember';
import { PresenceService } from '../../../services/presence/presence.service';
import { CounterBadgeComponent } from '../counter-badge/counter-badge/counter-badge.component';
import { UserCardTestIds } from './user-card.test-ids';
import { CleanReplyPipe } from '../../pipes/clean-reply.pipe';
import { RoomListAccessService } from '../../../services/room-list/room-list-access.service';
import { RoomHelperService } from '../../../services/room/room-helper.service';

@Component({
  selector: 'app-user-card',
  imports: [
    MatCard,
    MatCardHeader,
    MatCardTitle,
    DatePipe,
    UserIconComponent,
    AsyncPipe,
    MatCheckbox,
    MatIcon,
    CounterBadgeComponent
  ],
  templateUrl: './user-card.component.html',
  styleUrl: './user-card.component.scss'
})
/**
 * Custom Component für das Erstellen von Cards.
 * Begründung: Dies ist lediglich eine CustomComponent.
 * Es wird keine AFO dadurch erfüllt.
 */
export class UserCardComponent implements OnInit {
  cardDataTestid = input<string>();
  cardHeaderDataTestid = input<string>();
  cardTitleDataTestid = input<string>();
  cardContentDataTestid = input<string>();
  cardTitle = input<string>();
  hasEncryptionState = input<boolean>();
  roomId = input.required<string>();
  isActive = input<boolean>();
  isAbandoned = input<boolean>();
  collapsed = input.required<boolean>();
  isDeletionDialog = input<boolean>();
  currentUserId = input.required<string>();
  modelroom: Modelroom | undefined;
  checkedWithId = output<{ checkedId: string; isChecked: boolean }>();
  readonly spoilerChunks = inject(SpoilerChunksPipe);

  protected readonly testIds = UserCardTestIds;
  protected readonly lastMessageService = inject(LastMessageService);
  protected readonly mediaService = inject(MediaService);
  protected readonly userService = inject(UserService);
  protected readonly presenceService = inject(PresenceService);
  protected readonly roomService = inject(RoomHelperService);
  protected readonly roomListAccessService = inject(RoomListAccessService);
  protected readonly DateUtils = DateUtils;

  get roomname(): string {
    return this.roomService.getRoomname(this.roomId(), this.cardTitle())!;
  }

  ngOnInit(): void {
    this.userService
      .initialLoadRoomData(this.roomId())
      .finally(() => this.userService.userIconInitialLoad.set(this.roomId(), true));
    this.modelroom = this.roomListAccessService.getModelRoom(this.roomId());
  }

  getJoinedMembers(): RoomMember[] {
    if (!this.modelroom) {
      throw new Error('No Room found');
    }
    return this.modelroom.members.filter(member => member.membershipState === RoomMembershipState.Join);
  }

  onCheckboxChange(event: MatCheckboxChange): void {
    if (!this.modelroom) {
      throw new Error('No Room found');
    }
    this.checkedWithId.emit({
      checkedId: this.modelroom?.roomId,
      isChecked: event.checked
    });
  }

  containsSpoiler(body: string): boolean {
    const chunks = this.spoilerChunks.transform(body);
    return chunks.some(chunk => chunk.isSpoiler);
  }

  /**
   * Gibt die erste ID des Raummitgliedes wieder, welche nicht der CurrentUser ist.
   * @param joinedMembers
   */
  getOtherRoommemberId(joinedMembers: RoomMember[]): string {
    const otherMember = joinedMembers.find(member => member.mxid !== this.currentUserId());
    if (!otherMember) {
      throw new Error('No other member found, when calling getOtherRoommemberId');
    }
    return otherMember.mxid;
  }

  getMessagePreview(lastMessage: LastMessage): string {
    if (!lastMessage.body || lastMessage.body.trim() === '') {
      return '';
    }

    let displayName = '';
    if (lastMessage.senderId === this.currentUserId()) {
      displayName = 'Sie';
    } else if (lastMessage.sender && lastMessage.sender.trim() !== '') {
      displayName = lastMessage.sender;
    } else if (lastMessage.senderId) {
      displayName = lastMessage.senderId.split(':')[0].replace('@', '');
    }

    const message = displayName ? displayName + ': ' + lastMessage.body : lastMessage.body;

    return (
      message
        .replaceAll(/<br\s*\/?>/gi, ' ')
        .replaceAll(/<\/?p>/gi, ' ')
        .replaceAll('\n', ' ') ?? ''
    );
  }
}
